/** The fields of a scope (`OrgScope`) a place is built from. */
export interface PlaceScope {
    asset_uid: string
    asset_name?: string | null
    asset_type_code?: string | null
    role_code: string
    asset_address?: string | null
}

/** The badges a place card carries: never the word "tenant". */
export type PlaceBadge = 'owner' | 'resident'

/** One place of "My places": an asset, whatever the number of grants on it. */
export interface Place {
    asset_uid: string
    asset_name: string
    asset_type_code: string
    asset_address?: string
    badges: PlaceBadge[]
}

const BADGE_OF_ROLE: Record<string, PlaceBadge> = {
    property_owner: 'owner',
    resident: 'resident',
}

const BADGE_ORDER: PlaceBadge[] = ['owner', 'resident']

/**
 * badgesForRoles maps grant roles to card badges: `property_owner` reads
 * Owner, `resident` reads Resident, both when both grants exist. Other roles
 * (a provider's) carry no badge.
 */
export function badgesForRoles(roleCodes: readonly string[]): PlaceBadge[] {
    const found = new Set(roleCodes.map((code) => BADGE_OF_ROLE[code]).filter(Boolean))

    return BADGE_ORDER.filter((badge) => found.has(badge))
}

/**
 * groupScopesByAsset folds the caller's grants into places, one per asset, in
 * the order the API sent them (by name). An owner who lives in their lot has
 * two grants on it and one card.
 */
export function groupScopesByAsset(scopes: readonly PlaceScope[]): Place[] {
    const byAsset = new Map<string, { place: Place; roles: string[] }>()
    for (const scope of scopes) {
        let entry = byAsset.get(scope.asset_uid)
        if (!entry) {
            entry = {
                place: {
                    asset_uid: scope.asset_uid,
                    asset_name: scope.asset_name ?? '',
                    asset_type_code: scope.asset_type_code ?? '',
                    asset_address: scope.asset_address || undefined,
                    badges: [],
                },
                roles: [],
            }
            byAsset.set(scope.asset_uid, entry)
        }
        entry.roles.push(scope.role_code)
        if (!entry.place.asset_address && scope.asset_address) {
            entry.place.asset_address = scope.asset_address
        }
    }

    return [...byAsset.values()].map(({ place, roles }) => ({ ...place, badges: badgesForRoles(roles) }))
}

/**
 * solePlaceUid is the asset a resident is sent straight to: set only when
 * their grants reduce to one distinct asset.
 */
export function solePlaceUid(places: readonly Place[]): string | undefined {
    return places.length === 1 ? places[0].asset_uid : undefined
}
